import { useCallback } from 'react';
import { useRouter } from '@uirouter/react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { tableDataVM } from '../../../../core/table/table_view_models';

// provider imports
import { ROUTE_UPDATE } from '../../provider_const';
import { PageStoreHook, ProviderDataItem, ProviderListAction } from '../provider_list_types';
import { providerDataAtom, providerPageStateAtom } from '../provider_list_atoms';
import providerRepo from '../../data/providerRepo';
import { AdminProviderSearchModel } from '../provider_list_types';

export const useProviderListStore = (): PageStoreHook => {
    const router = useRouter();
    const [state, setState] = useImmerAtom(providerPageStateAtom);
    const setList = useSetImmerAtom(providerDataAtom);

    const getListAsync = useCallback(
        async (query: AdminProviderSearchModel) => {
            // get list items and advance page state to LIST
            try {
                ngDelegate.wait(true, true);
                setState(draft => {
                    draft.loading = true;
                    draft.action = 'QUERY';
                    return draft;
                });
                const r = await providerRepo.getList(query);

                // success, set table data and switch to LIST mode
                setState(draft => {
                    draft.action = 'LIST';
                    return draft;
                });
                setList(() => tableDataVM<ProviderDataItem>(r as ProviderDataItem[]));
            } catch (err: any) {
                // err msg handled upstream
            } finally {
                ngDelegate.wait(false);
                setState(draft => {
                    draft.loading = false;
                    return draft;
                });
            }
        },
        [setList, setState],
    );

    const getList = useCallback(
        (query: AdminProviderSearchModel): void => {
            getListAsync(query);
        },
        [getListAsync],
    );

    const routeToEdit = useCallback(
        (rowItem: ProviderDataItem) => {
            router.stateService.go(ROUTE_UPDATE, { Id: rowItem.Id });
        },
        [router.stateService],
    );

    const setAction = useCallback(
        (action: ProviderListAction): void => {
            // used by QUERY to open the search form and hide the table
            setState(draft => {
                draft.action = action;
                return draft;
            });
        },
        [setState],
    );

    const showHelp = useCallback((): void => {
        ngDelegate.helpLink('admin-provider', 'Provider List');
    }, []);

    return {
        ...state,
        routeToEdit,
        getList,
        setAction,
        showHelp,
    };
};

export default useProviderListStore;
